/**
 * Inverter Check Sheet API Service
 * Handles all API calls for Inverter Check Sheet operations
 */

import backendApi from '../../../services/backendApi';

export interface InverterChecklistItem {
  id?: number;
  serial_number?: number;
  description: string;
  finding?: string;
  inv_01_status?: string;
  inv_02_status?: string;
  inv_03_status?: string;
  inv_04_status?: string;
  remarks?: string;
}

export interface InverterCheckSheetData {
  id?: number;
  site_code?: string;
  // Format metadata
  format_number?: string;
  revision_number?: string;
  effective_from?: string;
  // Form Information
  check_date: string;
  equipment?: string;
  frequency?: string;
  customer_name?: string;
  block_no?: string;
  site_name?: string;
  // Inverter Serial Numbers
  inverter_serial_01?: string;
  inverter_serial_02?: string;
  inverter_serial_03?: string;
  inverter_serial_04?: string;
  // Comments
  comments?: string;
  // Signatures
  check_by_name?: string;
  check_by_signature?: string;
  verified_by_name?: string;
  verified_by_signature?: string;
  site_manager_contractor_name?: string;
  site_manager_contractor_signature?: string;
  site_manager_owner_name?: string;
  site_manager_owner_signature?: string;
  // Checklist items
  checklist_items: InverterChecklistItem[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class InverterCheckSheetApi {
  private baseEndpoint = '/v1/inverter-check-sheet';

  /**
   * Get all check sheets for the current site
   */
  async getAll(siteCode?: string): Promise<ApiResponse<InverterCheckSheetData[]>> {
    try {
      const headers: Record<string, string> = {};
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      const response = await backendApi.makeRequest(this.baseEndpoint + '/', {
        method: 'GET',
        headers,
      });

      return {
        success: true,
        data: Array.isArray(response) ? response : response.results || [],
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch check sheets',
      };
    }
  }

  /**
   * Get a single check sheet by ID
   */
  async getById(id: number, siteCode?: string): Promise<ApiResponse<InverterCheckSheetData>> {
    try {
      const headers: Record<string, string> = {};
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      const response = await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'GET',
        headers,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch check sheet',
      };
    }
  }

  /**
   * Create a new check sheet
   */
  async create(data: InverterCheckSheetData, siteCode?: string): Promise<ApiResponse<InverterCheckSheetData>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      // Get current user ID to ensure user_id is set
      let userId: number | null = null;
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user && userResponse.user.id) {
          userId = userResponse.user.id;
        }
      } catch (error) {
}

      // Prepare payload
      const payload: any = {
        site_code: siteCode || data.site_code,
        // Format metadata
        format_number: data.format_number || 'AxM-EQP-01-25',
        revision_number: data.revision_number || '01',
        effective_from: data.effective_from || '',
        // Form Information
        check_date: data.check_date,
        equipment: data.equipment || 'Central Inverter',
        frequency: data.frequency || 'Monthly',
        customer_name: data.customer_name || '',
        block_no: data.block_no || '',
        site_name: data.site_name || '',
        // Inverter Serial Numbers
        inverter_serial_01: data.inverter_serial_01 || '',
        inverter_serial_02: data.inverter_serial_02 || '',
        inverter_serial_03: data.inverter_serial_03 || '',
        inverter_serial_04: data.inverter_serial_04 || '',
        // Comments
        comments: data.comments || '',
        // Signatures
        check_by_name: data.check_by_name || '',
        check_by_signature: data.check_by_signature || '',
        verified_by_name: data.verified_by_name || '',
        verified_by_signature: data.verified_by_signature || '',
        site_manager_contractor_name: data.site_manager_contractor_name || '',
        site_manager_contractor_signature: data.site_manager_contractor_signature || '',
        site_manager_owner_name: data.site_manager_owner_name || '',
        site_manager_owner_signature: data.site_manager_owner_signature || '',
        // Checklist items
        checklist_items: data.checklist_items.map((item, index) => ({
          serial_number: item.serial_number || index + 1,
          description: item.description,
          finding: item.finding || '',
          inv_01_status: item.inv_01_status || '',
          inv_02_status: item.inv_02_status || '',
          inv_03_status: item.inv_03_status || '',
          inv_04_status: item.inv_04_status || '',
          remarks: item.remarks || '',
        })),
      };

      // Add user_id if available
      if (userId) {
        payload.user_id = userId;
      }

      const response = await backendApi.makeRequest(this.baseEndpoint + '/', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      return {
        success: true,
        data: response,
        message: 'Inverter check sheet created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create check sheet',
      };
    }
  }

  /**
   * Update an existing check sheet
   */
  async update(
    id: number,
    data: Partial<InverterCheckSheetData>,
    siteCode?: string
  ): Promise<ApiResponse<InverterCheckSheetData>> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      // Get current user ID to ensure user_id is set
      let userId: number | null = null;
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user && userResponse.user.id) {
          userId = userResponse.user.id;
        }
      } catch (error) {
}

      // Prepare payload - only include defined fields
      const payload: any = {};
      
      if (data.format_number !== undefined) payload.format_number = data.format_number;
      if (data.revision_number !== undefined) payload.revision_number = data.revision_number;
      if (data.effective_from !== undefined) payload.effective_from = data.effective_from;
      if (data.check_date !== undefined) payload.check_date = data.check_date;
      if (data.equipment !== undefined) payload.equipment = data.equipment;
      if (data.frequency !== undefined) payload.frequency = data.frequency;
      if (data.customer_name !== undefined) payload.customer_name = data.customer_name;
      if (data.block_no !== undefined) payload.block_no = data.block_no;
      if (data.site_name !== undefined) payload.site_name = data.site_name;
      if (data.inverter_serial_01 !== undefined) payload.inverter_serial_01 = data.inverter_serial_01;
      if (data.inverter_serial_02 !== undefined) payload.inverter_serial_02 = data.inverter_serial_02;
      if (data.inverter_serial_03 !== undefined) payload.inverter_serial_03 = data.inverter_serial_03;
      if (data.inverter_serial_04 !== undefined) payload.inverter_serial_04 = data.inverter_serial_04;
      if (data.comments !== undefined) payload.comments = data.comments;
      if (data.check_by_name !== undefined) payload.check_by_name = data.check_by_name;
      if (data.check_by_signature !== undefined) payload.check_by_signature = data.check_by_signature;
      if (data.verified_by_name !== undefined) payload.verified_by_name = data.verified_by_name;
      if (data.verified_by_signature !== undefined) payload.verified_by_signature = data.verified_by_signature;
      if (data.site_manager_contractor_name !== undefined) payload.site_manager_contractor_name = data.site_manager_contractor_name;
      if (data.site_manager_contractor_signature !== undefined) payload.site_manager_contractor_signature = data.site_manager_contractor_signature;
      if (data.site_manager_owner_name !== undefined) payload.site_manager_owner_name = data.site_manager_owner_name;
      if (data.site_manager_owner_signature !== undefined) payload.site_manager_owner_signature = data.site_manager_owner_signature;

      // Add user_id if available
      if (userId) {
        payload.user_id = userId;
      }

      // Include checklist items if provided
      if (data.checklist_items) {
        payload.checklist_items = data.checklist_items.map((item, index) => ({
          serial_number: item.serial_number || index + 1,
          description: item.description,
          finding: item.finding || '',
          inv_01_status: item.inv_01_status || '',
          inv_02_status: item.inv_02_status || '',
          inv_03_status: item.inv_03_status || '',
          inv_04_status: item.inv_04_status || '',
          remarks: item.remarks || '',
        }));
      }

      const response = await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });

      return {
        success: true,
        data: response,
        message: 'Inverter check sheet updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update check sheet',
      };
    }
  }

  /**
   * Delete a check sheet
   */
  async delete(id: number, siteCode?: string): Promise<ApiResponse<void>> {
    try {
      const headers: Record<string, string> = {};
      if (siteCode) {
        headers['X-Site-Code'] = siteCode;
      }

      await backendApi.makeRequest(`${this.baseEndpoint}/${id}/`, {
        method: 'DELETE',
        headers,
      });

      return {
        success: true,
        message: 'Inverter check sheet deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete check sheet',
      };
    }
  }
}

export default new InverterCheckSheetApi();

